import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  LocationProps,
  LocationSection,
  TimeProps,
} from 'src/components/organisms';
import { TimeframeInput } from 'src/graphql';
import { FormState, GlobalFilterProps, State } from 'src/types';
import { isMatchingArray } from 'src/utils';
import { stringArraysMatchAsMultisets } from 'src/utils/boxFilterFullSelection';
import { diff, getYear } from 'src/utils/time';

const pruneStaleSelections = (
  selections: string[][],
  sections: LocationSection[],
): string[][] => {
  const pruned = selections.map((sel, i) => {
    const validOptions = new Set(sections[i]?.options ?? []);
    return sel.filter((item) => validOptions.has(item));
  });
  const changed = pruned.some((sel, i) => sel.length !== selections[i].length);
  return changed ? pruned : selections;
};

export const useGlobalFilters = (args: LocationProps | TimeProps) => {
  const storedGlobalFilters = useSelector(
    (state: State) => state.insightsGlobalFilters[args.dashboard],
  );
  const filteringByLocation = args.type === GlobalFilterProps.LOCATION;
  const filteringByTime = args.type === GlobalFilterProps.TIME;

  const initialSections = filteringByLocation
    ? args.sections.map((s) => s.selectedOptions)
    : [];
  const [sectionSelections, setSectionSelections] =
    useState<string[][]>(initialSections);

  const [customSelectedTimeframe, setCustomSelectedTimeframe] = useState(
    filteringByTime ? args.customSelectedTimeframe : undefined,
  );
  const [timeOptionSelected, setTimeOptionSelected] = useState(
    filteringByTime ? args.selectedTimeOption : undefined,
  );
  const [customTimeState, setCustomTimeState] = useState(
    filteringByTime && args.selectedTimeOption === 'custom'
      ? FormState.SUCCESS
      : FormState.DEFAULT,
  );
  const [applyButtonDisabled, setApplyButtonDisabled] = useState(true);
  const timeframeYears = filteringByTime
    ? new Array(getYear() - args.oldestSelectableYear + 1)
        .fill(args.oldestSelectableYear)
        .map((year, i) => `${year + i}`)
    : undefined;

  const [prevCategory, setPrevCategory] = useState(
    filteringByLocation ? args.locationFilterCategory : undefined,
  );

  const handleSectionChange = (sectionIndex: number, option: string) => {
    if (filteringByLocation) {
      const section = args.sections[sectionIndex];
      if (section?.disabledOptions?.includes(option)) return;
    }

    setSectionSelections((prev) => {
      const updated = [...prev];
      const current = updated[sectionIndex] ?? [];
      const wasSelected = current.includes(option);
      updated[sectionIndex] = wasSelected
        ? current.filter((o) => o !== option)
        : [...current, option];
      if (filteringByLocation && args.onCrossSelect) {
        const crossSelected = args.onCrossSelect(
          sectionIndex,
          option,
          !wasSelected,
          updated,
        );
        if (crossSelected) {
          const otherIndex = sectionIndex === 0 ? 1 : 0;
          updated[otherIndex] = crossSelected;
        }
      }

      return updated;
    });
  };

  const handleSectionSelectAll = (sectionIndex: number) => {
    if (!filteringByLocation) return;
    const section = args.sections[sectionIndex];
    if (!section) return;
    const disabledSet = new Set(section.disabledOptions ?? []);
    const enabledOptions = section.options.filter((o) => !disabledSet.has(o));
    setSectionSelections((prev) => {
      const updated = [...prev];
      const current = updated[sectionIndex] ?? [];
      updated[sectionIndex] = stringArraysMatchAsMultisets(
        current,
        enabledOptions,
      )
        ? []
        : [...enabledOptions];
      if (sectionIndex === 0 && args.onAfterRegionBulkSelect) {
        const synced = args.onAfterRegionBulkSelect(updated, enabledOptions);
        if (synced !== undefined) {
          return synced;
        }
      }
      if (sectionIndex === 1 && args.onAfterCountryBulkSelect) {
        const synced = args.onAfterCountryBulkSelect(updated, enabledOptions);
        if (synced !== undefined) {
          return synced;
        }
      }
      return updated;
    });
  };

  const handleCustomTimeFilterChange = (time: TimeframeInput) => {
    setCustomSelectedTimeframe(time);
    setTimeOptionSelected('custom');
    if (time?.start && time?.end && diff(time.start, time.end) > 0) {
      setCustomTimeState(FormState.ERROR);
    } else {
      setCustomTimeState(FormState.SUCCESS);
    }
  };

  const handleYearSelectChange = (year: string) => {
    setTimeOptionSelected(year);
    setCustomTimeState(FormState.DEFAULT);
  };

  const clearFilters = () => {
    if (filteringByLocation) {
      setSectionSelections(args.sections.map((s) => s.resetToState));
    } else {
      setTimeOptionSelected(args.resetToState.option);
      if (args.resetToState.customTimeframe) {
        setCustomSelectedTimeframe(args.resetToState.customTimeframe);
        setCustomTimeState(FormState.SUCCESS);
      } else {
        setCustomTimeState(FormState.DEFAULT);
      }
    }
  };

  const segmentTrackingFilter = filteringByLocation
    ? storedGlobalFilters.locationFilterCategory
    : 'time';

  useEffect(() => {
    if (filteringByLocation && args.onSelectionsChange) {
      args.onSelectionsChange(sectionSelections);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionSelections]);

  const sectionOptionsKey = filteringByLocation
    ? args.sections.map((s) => s.options.join('\0')).join('\n')
    : '';

  useEffect(() => {
    if (!filteringByLocation) return;
    setSectionSelections((prev) => pruneStaleSelections(prev, args.sections));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionOptionsKey]);

  useEffect(() => {
    if (filteringByLocation) {
      if (args.locationFilterCategory !== prevCategory) {
        setSectionSelections(args.sections.map((s) => s.resetToState));
        setPrevCategory(args.locationFilterCategory);
        setApplyButtonDisabled(false);
        return;
      }

      const anyEmpty = sectionSelections.some((sel) => sel.length === 0);
      if (anyEmpty) {
        setApplyButtonDisabled(true);
      } else {
        const allMatch = args.sections.every((section, i) =>
          isMatchingArray(sectionSelections[i] ?? [], section.selectedOptions),
        );
        const categoryMatch =
          args.locationFilterCategory === undefined ||
          args.locationFilterCategory ===
            storedGlobalFilters.locationFilterCategory;
        setApplyButtonDisabled(allMatch && categoryMatch);
      }
    } else if (customTimeState === FormState.ERROR) {
      setApplyButtonDisabled(true);
    } else if (timeOptionSelected === 'custom') {
      const newCustomSelectedTimeframeStart = new Date(
        customSelectedTimeframe?.start,
      ).getTime();
      const newCustomSelectedTimeframeEnd = new Date(
        customSelectedTimeframe?.end,
      ).getTime();
      const argsCustomSelectedTimeframeStart = new Date(
        args.customSelectedTimeframe.start,
      ).getTime();
      const argsCustomSelectedTimeframeEnd = new Date(
        args.customSelectedTimeframe.end,
      ).getTime();
      const sameStartTimeSelected =
        newCustomSelectedTimeframeStart === argsCustomSelectedTimeframeStart;
      const sameEndTimeSelected =
        newCustomSelectedTimeframeEnd === argsCustomSelectedTimeframeEnd;

      const disableApplyButton =
        sameStartTimeSelected &&
        sameEndTimeSelected &&
        args.selectedTimeOption === 'custom';
      setApplyButtonDisabled(disableApplyButton);
    } else {
      const disableApplyButton = timeOptionSelected === args.selectedTimeOption;
      setApplyButtonDisabled(disableApplyButton);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sectionSelections,
    customTimeState,
    timeOptionSelected,
    customSelectedTimeframe,
    storedGlobalFilters,
  ]);

  return {
    sectionSelections,
    customSelectedTimeframe,
    timeOptionSelected,
    customTimeState,
    applyButtonDisabled,
    timeframeYears,
    handleSectionChange,
    handleSectionSelectAll,
    handleCustomTimeFilterChange,
    handleYearSelectChange,
    clearFilters,
    segmentTrackingFilter,
  };
};
